import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import Button from '../../components/Button';
import MetaList, { MetaListItem } from '../../components/MetaList';
import Timestamp from '../../components/Timestamp';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { Scalars } from '../../graphql/generated/graphql';
import getFirstName from '../../utils/text/getFirstName';
import userPath from '../../utils/url/userPath';
import useIsPublicView from '../../utils/useIsPublicView';
import * as styles from './CommentAuthor.module.scss';
import Link from '../../utils/router/Link';

const AUTHOR_FRAGMENT = gql(`
  fragment CommentAuthor_author on PublicUserFields {
    id
    name
    ...userPath_user
  }
`);

interface CommentAuthorProps {
  author: DocumentType<typeof AUTHOR_FRAGMENT>;
  isFirst?: boolean;
  isExpanded?: boolean;
  insertedAt: Scalars['Datetime']['output'];
  updatedAt: Scalars['Datetime']['output'];
  copyThreadLink?: () => void;
}

const CommentAuthor = ({
  author,
  isFirst = false,
  isExpanded = false,
  insertedAt,
  updatedAt,
  copyThreadLink,
}: CommentAuthorProps) => {
  const isPublicView = useIsPublicView();

  const isEdited = updatedAt && insertedAt !== updatedAt;

  if (isExpanded) {
    const timestamp = (
      <>
        <Timestamp date={insertedAt} />
        {!!isEdited && ' (edited)'}
      </>
    );

    return (
      <MetaList>
        <MetaListItem delimiter={null}>
          {isPublicView ? (
            <span className="name">{getFirstName(author.name)}</span>
          ) : (
            <Link
              target="_blank"
              rel="noopener noreferrer"
              className="name"
              to={userPath(author)}
            >
              {getFirstName(author.name)}
            </Link>
          )}
        </MetaListItem>
        <MetaListItem>
          <Tooltip
            content={
              <div className={styles.tooltip}>
                {isEdited ? (
                  <>
                    <div className={styles.labels}>
                      <Tooltip.Muted>Created:</Tooltip.Muted>
                      <Tooltip.Muted>Edited:</Tooltip.Muted>
                    </div>
                    <div className={styles.timestamps}>
                      <span>
                        {moment(insertedAt).format('MMM Do, YYYY, h:mm A')}
                      </span>
                      <span>
                        {moment(updatedAt).format('MMM Do, YYYY, h:mm A')}
                      </span>
                    </div>
                  </>
                ) : (
                  moment(insertedAt).format('MMM Do, YYYY, h:mm A')
                )}
              </div>
            }
          >
            {isFirst && copyThreadLink ? (
              <Button
                onClick={() => copyThreadLink()}
                className={classNames(styles.timestamp, styles.timestampButton)}
              >
                {timestamp}
              </Button>
            ) : (
              <span className={styles.timestamp}>{timestamp}</span>
            )}
          </Tooltip>
        </MetaListItem>
      </MetaList>
    );
  }
  return (
    <Link className="name" to={userPath(author)}>
      {author.name}
    </Link>
  );
};

export default CommentAuthor;
